import React, { useEffect } from 'react';
import {
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  IconButton,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';

import { useAuthContext } from '../../context/auth-context';

/**
 * Filtro de afiliación para los reportes.
 *
 * Filtra por zona (`affiliationZone`), que es el único eje que varía dentro
 * de un campamento — ver `utils/report-filters.js`. Cadena vacía = "Todas".
 *
 * Para un Secretario de Zona el filtro deja de ser una elección: el servidor ya le
 * devuelve solo su zona (`zoneScope`), así que ofrecerle "Todas" u otra zona sería
 * mentirle — elegiría y no cambiaría nada. Se muestra fijado a su zona y bloqueado.
 * El acotado real es del servidor; esto solo hace que la pantalla diga la verdad.
 */
export default function AffiliationFilter({
  value,
  onChange,
  zones = [],
  label = 'Afiliación (Zona)',
  size = 'medium',
  disabled = false,
  fullWidth = true,
}) {
  // `effectiveZone`, no `user.zone`: bajo "Ver como sec · Zona X" el usuario sigue
  // siendo el administrador real, con `zone: null`, así que el filtro mostraba
  // "Todas" mientras el servidor devolvía solo la Zona X — el caso exacto que la
  // vista previa existe para comprobar.
  const { effectiveZone, hasPermission } = useAuthContext();

  // Mismo criterio que `zoneScope`: tener zona y no poder ver todas las zonas.
  const zonaFija = Boolean(effectiveZone) && !hasPermission('reports.view-all-zones');
  const opciones = zonaFija ? [effectiveZone] : zones;

  // Si el filtro del padre arrancara en "Todas", la etiqueta diría "Todas" sobre unos
  // datos que solo traen una zona. Se fija una vez; el guard corta la reentrada.
  useEffect(() => {
    if (zonaFija && value !== effectiveZone) onChange(effectiveZone);
  }, [zonaFija, effectiveZone, value, onChange]);

  // Con la zona fijada el select nunca queda vacío —siempre muestra su zona—, así que
  // "sin datos" se mide sobre las filas recibidas, no sobre las opciones del select.
  const sinFilas = zones.length === 0;
  const ayuda = zonaFija
    ? sinFilas
      ? 'No hay datos de tu zona en este reporte.'
      : `Solo ves los datos de tu zona (${effectiveZone}).`
    : sinFilas
      ? 'Los datos cargados no traen ninguna zona.'
      : null;

  return (
    <FormControl fullWidth={fullWidth} size={size} disabled={disabled || zonaFija || sinFilas}>
      <InputLabel>{label}</InputLabel>
      <Select
        value={zonaFija ? effectiveZone : (value ?? '')}
        label={label}
        onChange={(e) => onChange(e.target.value)}
        endAdornment={
          value && !zonaFija ? (
            <InputAdornment position="end" sx={{ mr: 2 }}>
              <IconButton size="small" edge="end" onClick={() => onChange('')} aria-label="Limpiar filtro de afiliación">
                <ClearIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : null
        }
      >
        {/* "Todas" solo existe para quien puede ver más de una zona. */}
        {!zonaFija && <MenuItem value="">Todas</MenuItem>}
        {opciones.map((zone) => (
          <MenuItem key={zone} value={zone}>{zone}</MenuItem>
        ))}
      </Select>
      {ayuda && <FormHelperText>{ayuda}</FormHelperText>}
    </FormControl>
  );
}
